import {
  CaretDown,
  Copy,
  Database,
  Funnel,
  MagnifyingGlass,
  Star,
  Table,
} from "@phosphor-icons/react"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { MasonryWidget } from "./MasonryWidget"

const schemaData = [
  { field: "block_slot", type: "INTEGER", mode: "NULLABLE" },
  { field: "block_hash", type: "STRING", mode: "NULLABLE" },
  { field: "block_height", type: "INTEGER", mode: "NULLABLE" },
  { field: "block_timestamp", type: "TIMESTAMP", mode: "NULLABLE" },
  { field: "parent_slot", type: "INTEGER", mode: "NULLABLE" },
  { field: "previous_block_hash", type: "STRING", mode: "NULLABLE" },
  { field: "total_transaction_count", type: "INTEGER", mode: "NULLABLE" },
  { field: "successful_transaction_count", type: "INTEGER", mode: "NULLABLE" },
]

const tabs = [
  { label: "Schema", active: true },
  { label: "Details", active: false },
  { label: "Preview", active: false },
]

export function MasonryBigQueryViewer() {
  return (
    <MasonryWidget
      href="/products/datashare"
      icon={Database}
      productName="Datashare"
      widgetName="Access our data warehouse"
      description="Query raw blockchain data directly in your own data warehouse with schemas for blocks, transactions, and decoded contract events."
      height={500}
      className="rounded-2xl"
      featuredVisual={
        <div
          className="relative border-t border-l border-solid border-border-default rounded-tl-xl overflow-hidden flex-1 min-h-0"
          style={FADE_MASK_STYLE}
        >
          <div
            className="bg-white dark:bg-[#0a0a0a] border-neutral-200 dark:border-neutral-800"
            style={{ width: "calc(100% + 100px)", minWidth: "500px" }}
          >
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
                  <CaretDown className="w-2.5 h-2.5 text-neutral-400 dark:text-neutral-500" />
                </div>
                <div className="w-px h-3 bg-neutral-300 dark:bg-neutral-700" />
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-800 rounded-sm">
                  <Table className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                  <span className="text-xs text-neutral-700 dark:text-neutral-300">blocks</span>
                  <CaretDown className="w-2.5 h-2.5 text-neutral-400 dark:text-neutral-500" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-800 text-[10px]">
              <span className="text-blue-500 dark:text-blue-400">blockchain</span>
              <span className="text-neutral-400 dark:text-neutral-500">/</span>
              <span className="text-neutral-500 dark:text-neutral-400">Datasets</span>
              <span className="text-neutral-400 dark:text-neutral-500">/</span>
              <span className="text-blue-500 dark:text-blue-400">solana</span>
              <span className="text-neutral-400 dark:text-neutral-500">/</span>
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">blocks</span>
            </div>

            <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
                <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                  blocks
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button className="flex items-center gap-1 px-2 py-1 text-[10px] text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
                  <MagnifyingGlass className="w-3 h-3" />
                  Query
                </button>
                <button className="flex items-center gap-1 px-2 py-1 text-[10px] text-blue-500 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-sm">
                  <Copy className="w-3 h-3" />
                  Copy
                </button>
              </div>
            </div>

            <div className="flex items-center gap-0 px-3 border-b border-neutral-200 dark:border-neutral-800">
              {tabs.map((tab) => (
                <button
                  key={tab.label}
                  className={`px-3 py-2 text-[10px] border-b-2 ${
                    tab.active
                      ? "text-blue-500 dark:text-blue-400 border-blue-500 dark:border-blue-400"
                      : "text-neutral-500 dark:text-neutral-400 border-transparent hover:text-neutral-700 dark:hover:text-neutral-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 dark:border-neutral-800">
              <Funnel className="w-3 h-3 text-neutral-400 dark:text-neutral-500" />
              <span className="text-[10px] text-neutral-500 dark:text-neutral-400">Filter</span>
              <span className="text-[10px] text-neutral-400 dark:text-neutral-600">
                Enter property name or value
              </span>
            </div>

            <div className="grid grid-cols-[24px_1fr_80px_70px] gap-0 px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-800 text-[9px] text-neutral-500 dark:text-neutral-400">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="w-3 h-3 rounded-sm border-neutral-300 dark:border-neutral-600 bg-transparent"
                  readOnly
                />
              </div>
              <div>Field name</div>
              <div>Type</div>
              <div>Mode</div>
            </div>

            <div className="flex flex-col">
              {schemaData.map((row) => (
                <div
                  key={row.field}
                  className="grid grid-cols-[24px_1fr_80px_70px] gap-0 px-3 py-1.5 border-b border-neutral-200/50 dark:border-neutral-800/50 text-[10px] hover:bg-neutral-100/50 dark:hover:bg-neutral-800/30"
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-3 h-3 rounded-sm border-neutral-300 dark:border-neutral-600 bg-transparent"
                      readOnly
                    />
                  </div>
                  <div className="text-neutral-700 dark:text-neutral-300 font-mono text-[9px] truncate">
                    {row.field}
                  </div>
                  <div className="text-neutral-500 dark:text-neutral-400 font-mono text-[9px]">
                    {row.type}
                  </div>
                  <div className="text-neutral-500 dark:text-neutral-400 text-[9px]">
                    {row.mode}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
    />
  )
}
